import React, { useEffect, useState } from 'react';
import {
  Alert, Card, Select, Spinner, useChartTheme, useIsMobile, monthAxes, monthChartHeight, compactTry,
} from '../../../core/ui';
import ReactECharts from 'echarts-for-react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from '../../../core/context/TranslationContext';
import { getFamilyDebtForecast } from '../api/family';
import { useFamily } from '../context/FamilyContext';
import { memberColor } from '../format';
import styles from './FamilyForecast.module.css';

export default function FamilyForecast() {
  const { t } = useTranslation();
  const { family, loading: familyLoading } = useFamily();
  const chart = useChartTheme();
  const vertical = useIsMobile();
  const [months, setMonths] = useState(12);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!family) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getFamilyDebtForecast(months);
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.response?.data?.message || t('error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [months, family, t]);

  if (familyLoading) return <Spinner className={styles.m4} />;
  if (!family) return <Navigate to="/family/create" replace />;

  const allMembers = [...new Map(
    rows.flatMap(r => r.byMember || []).map(m => [m.userId, m])
  ).values()];

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: chart.bgElevated,
      borderColor: chart.border,
      textStyle: { color: chart.text, fontSize: 12 },
    },
    legend: {
      type: vertical ? 'scroll' : 'plain',
      data: allMembers.map(m => m.username),
      textStyle: { color: chart.textMuted, fontSize: 12 },
      icon: 'circle',
    },
    grid: { left: '3%', right: '4%', bottom: vertical ? '3%' : '10%', containLabel: true },
    ...monthAxes(vertical, {
      type: 'category',
      data: rows.map(r => r.month),
      axisLabel: { color: chart.textMuted, rotate: -30, fontSize: 11 },
      axisLine: { lineStyle: { color: chart.border } },
      axisTick: { show: false },
    }, {
      type: 'value',
      axisLabel: { color: chart.textMuted, fontSize: 11, formatter: compactTry },
      splitLine: { lineStyle: { color: chart.border } },
      axisLine: { show: false },
      axisTick: { show: false },
    }),
    series: allMembers.map(member => ({
      name: member.username,
      type: 'bar',
      stack: 'total',
      itemStyle: { color: memberColor(member.username) },
      data: rows.map(r => {
        const m = (r.byMember || []).find(bm => bm.userId === member.userId);
        return m ? Number(m.amount) : 0;
      }),
      barMaxWidth: 32,
    })),
  };

  return (
    <div className={styles.container}>
      <h4>{t('family.forecast')}</h4>
      <div className={styles.mb3}>
        <Select
          value={months}
          onChange={e => setMonths(Number(e.target.value))}
          className={styles.select}
        >
          <option value={6}>{t('budget.sixMonths')}</option>
          <option value={12}>{t('budget.twelveMonths')}</option>
          <option value={24}>{t('budget.twentyFourMonths')}</option>
        </Select>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? <Spinner /> : (
        <Card>
          <Card.Body>
            <ReactECharts option={option} style={{ height: monthChartHeight(vertical, rows.length, 350) }} />
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
